import axios, { AxiosError } from "axios";
import {
  useQuery,
  UseQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "react-query";

import {
  CreateLeaveExportJobRequest,
  CreateLeaveExportJobResponse,
  LeaveExportsListApiResponse,
} from "../../utils/types";

// Service function to fetch leave exports
const fetchLeaveExports = async (
  page: number
): Promise<LeaveExportsListApiResponse> => {
  const params = { page };
  const url = `/employee-tickets/exports`;
  const { data } = await axios.get(url, { params });
  return data;
};

// Service function to create a leave export job
const createLeaveExportJob = async (
  request: CreateLeaveExportJobRequest
): Promise<CreateLeaveExportJobResponse> => {
  const url = `/employee-tickets/exports/create/`;
  const params = {
    job_name: request.jobName,
  };
  const { data } = await axios.post(url, params);
  return data as CreateLeaveExportJobResponse;
};

// Hook to fetch leave exports
export const useLeaveExportsApiService = (
  page = 1
): UseQueryResult<LeaveExportsListApiResponse, AxiosError> =>
  useQuery<LeaveExportsListApiResponse, AxiosError>(
    ["LeaveExportsApiService", page],
    () => fetchLeaveExports(page),
    { keepPreviousData: true }
  );

// Hook to create a leave export job
export const useCreateLeaveExportJobApiService = (): UseMutationResult<
  CreateLeaveExportJobResponse,
  AxiosError,
  CreateLeaveExportJobRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<
    CreateLeaveExportJobResponse,
    AxiosError,
    CreateLeaveExportJobRequest
  >((request: CreateLeaveExportJobRequest) => createLeaveExportJob(request), {
    onSuccess: () => {
      // Invalidate or refetch the relevant queries
      queryClient.invalidateQueries(["LeaveExportsApiService"]);
    },
  });
};
